import { createTheme, type Shadows } from "@mui/material/styles";

const fontFamily = [
  "'Aeonik'",
  "'Inter'",
  "-apple-system",
  "BlinkMacSystemFont",
  "'Segoe UI'",
  "'Helvetica Neue'",
  "Arial",
  "sans-serif",
].join(", ");

export const locumColors = {
  teal: "#22D5D5",
  navy: "#211B35",
  purple: "#8D44F3",
  amber: "#F5A623",
  white: "#FFFFFF",
  lightGrey: "#F7F7F8",
  error: "#FF4D6A",
  success: "#00C896",
  navyDeep: "#0F0A1F",
  navyMid: "#2D2545",
  navyLine: "#3D3555",
  purpleDark: "#5821A8",
  purpleLight: "#C6A2F9",
  tealDark: "#00404C",
  tealLight: "#EBFFFF",
  errorDark: "#5F2120",
  errorLight: "#FDEDED",
  successDark: "#009E77",
  successLight: "#D6F7EE",
  warningMain: "#FFB400",
  warningDark: "#663C00",
  warningLight: "#FFF4E5",
  infoMain: "#014361",
  infoDark: "#01374F",
  infoLight: "#E5F6FD",
  pink: "#FFC9F2",
  textPrimary: "#211B35",
  textSecondary: "#4D476B",
  textDisabled: "rgba(33, 27, 53, 0.26)",
  textMuted: "rgba(33, 27, 53, 0.75)",
  textOnDark: "#FFFFFF",
  textOnDarkSecondary: "rgba(255, 255, 255, 0.85)",
  textOnDarkMuted: "rgba(255, 255, 255, 0.7)",
  textOnDarkSubtle: "rgba(255, 255, 255, 0.55)",
  textOnDarkFaint: "rgba(255, 255, 255, 0.4)",
  darkSurfaceHover: "rgba(255, 255, 255, 0.08)",
  darkSurfaceActive: "rgba(255, 255, 255, 0.05)",
  darkBorder: "rgba(255, 255, 255, 0.1)",
  darkBorderStrong: "rgba(255, 255, 255, 0.35)",
  tealBorderSubtle: "rgba(34, 213, 213, 0.3)",
  tealBgSubtle: "rgba(34, 213, 213, 0.09)",
  tealBorderMid: "rgba(34, 213, 213, 0.33)",
  tealGlow: "rgba(34, 213, 213, 0.53)",
  purpleBgSubtle: "rgba(141, 68, 243, 0.4)",
  lightSurfaceHover: "rgba(33, 27, 53, 0.04)",
  muted: "#A9A3BF",
  inputOutline: "rgba(0, 0, 0, 0.23)",
  tooltipBg: "rgba(97, 97, 97, 0.9)",
  navBorder: "#E5E5E5",
  gridLine: "rgba(34, 213, 213, 0.06)",
} as const;

export const locumTypography = {
  fontFamily,
  weightRegular: 400,
  weightMedium: 500,
  weightSemiBold: 600,
  weightBold: 700,
} as const;

export const locumShape = {
  spacingBase: 4,
  borderRadius: 4,
  cornerRadius: {
    sm: 4,
    md: 8,
    lg: 12,
    xl: 16,
    pill: 9999,
  },
} as const;

export const locumLayout = {
  maxWidth: 1100,
  containerPadding: "2rem",
  sectionPadding: "clamp(80px, 12vw, 160px)",
  cardGap: 24,
} as const;

export const locumMotion = {
  reveal: {
    hero: { duration: 0.9, ease: "power3.out" },
    section: { duration: 0.8, ease: "power3.out" },
    detail: { duration: 0.6, ease: "power2.out" },
  },
  interaction: {
    hover: { duration: 0.3, ease: "power2.out" },
    expand: { duration: 0.5, ease: "cubic.inOut" },
  },
  stagger: {
    words: 0.05,
    cards: 0.08,
  },
  transition: {
    fast: "all 0.3s ease",
    default: "all 0.5s ease",
  },
} as const;

const shadows: Shadows = [
  "none",
  "0px 2px 4px -1px rgba(0,0,0,0.12), 0px 1px 6px 0px rgba(0,0,0,0.08)",
  "0px 3px 3px -2px rgba(0,0,0,0.15), 0px 2px 3px 0px rgba(0,0,0,0.1), 0px 1px 7px 0px rgba(0,0,0,0.1)",
  "0px 3px 9px 0px rgba(0,0,0,0.1), 0px 2px 12px 0px rgba(0,0,0,0.1)",
  "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
  "0px 3px 5px -1px rgba(0,0,0,0.2), 0px 5px 8px 0px rgba(0,0,0,0.14), 0px 1px 14px 0px rgba(0,0,0,0.12)",
  "0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)",
  "0px 4px 5px -2px rgba(0,0,0,0.2), 0px 7px 10px 1px rgba(0,0,0,0.14), 0px 2px 16px 1px rgba(0,0,0,0.12)",
  "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)",
  "0px 5px 6px -3px rgba(0,0,0,0.2), 0px 9px 12px 1px rgba(0,0,0,0.14), 0px 3px 16px 2px rgba(0,0,0,0.12)",
  "0px 6px 6px -3px rgba(0,0,0,0.2), 0px 10px 14px 1px rgba(0,0,0,0.14), 0px 4px 18px 3px rgba(0,0,0,0.12)",
  "0px 6px 7px -4px rgba(0,0,0,0.2), 0px 11px 15px 1px rgba(0,0,0,0.14), 0px 4px 20px 3px rgba(0,0,0,0.12)",
  "0px 7px 8px -4px rgba(0,0,0,0.2), 0px 12px 17px 2px rgba(0,0,0,0.14), 0px 5px 22px 4px rgba(0,0,0,0.12)",
  "0px 7px 8px -4px rgba(0,0,0,0.2), 0px 13px 19px 2px rgba(0,0,0,0.14), 0px 5px 24px 4px rgba(0,0,0,0.12)",
  "0px 7px 9px -4px rgba(0,0,0,0.2), 0px 14px 21px 2px rgba(0,0,0,0.14), 0px 5px 26px 4px rgba(0,0,0,0.12)",
  "0px 8px 9px -5px rgba(0,0,0,0.2), 0px 15px 22px 2px rgba(0,0,0,0.14), 0px 6px 28px 5px rgba(0,0,0,0.12)",
  "0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)",
  "0px 8px 11px -5px rgba(0,0,0,0.2), 0px 17px 26px 2px rgba(0,0,0,0.14), 0px 6px 32px 5px rgba(0,0,0,0.12)",
  "0px 9px 11px -5px rgba(0,0,0,0.2), 0px 18px 28px 2px rgba(0,0,0,0.14), 0px 7px 34px 6px rgba(0,0,0,0.12)",
  "0px 9px 12px -6px rgba(0,0,0,0.2), 0px 19px 29px 2px rgba(0,0,0,0.14), 0px 7px 36px 6px rgba(0,0,0,0.12)",
  "0px 10px 13px -6px rgba(0,0,0,0.2), 0px 20px 31px 3px rgba(0,0,0,0.14), 0px 8px 38px 7px rgba(0,0,0,0.12)",
  "0px 10px 13px -6px rgba(0,0,0,0.2), 0px 21px 33px 3px rgba(0,0,0,0.14), 0px 8px 40px 7px rgba(0,0,0,0.12)",
  "0px 10px 14px -6px rgba(0,0,0,0.2), 0px 22px 35px 3px rgba(0,0,0,0.14), 0px 8px 42px 7px rgba(0,0,0,0.12)",
  "0px 11px 14px -7px rgba(0,0,0,0.2), 0px 23px 36px 3px rgba(0,0,0,0.14), 0px 9px 44px 8px rgba(0,0,0,0.12)",
  "0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)",
];

export const locumTheme = createTheme({
  cssVariables: true,
  spacing: locumShape.spacingBase,
  palette: {
    mode: "light",
    primary: { main: locumColors.teal, dark: locumColors.tealDark, light: locumColors.tealLight, contrastText: locumColors.navy },
    secondary: { main: locumColors.purple, dark: locumColors.purpleDark, light: locumColors.purpleLight, contrastText: locumColors.white },
    error: { main: locumColors.error, dark: locumColors.errorDark, light: locumColors.errorLight, contrastText: locumColors.white },
    warning: { main: locumColors.warningMain, dark: locumColors.warningDark, light: locumColors.warningLight, contrastText: locumColors.white },
    info: { main: locumColors.infoMain, dark: locumColors.infoDark, light: locumColors.infoLight, contrastText: locumColors.white },
    success: { main: locumColors.success, dark: locumColors.successDark, light: locumColors.successLight, contrastText: locumColors.white },
    text: { primary: locumColors.textPrimary, secondary: locumColors.textSecondary, disabled: locumColors.textDisabled },
    background: { default: locumColors.white, paper: locumColors.white },
    divider: locumColors.navBorder,
    action: {
      active: "rgba(33, 27, 53, 0.54)",
      hover: locumColors.lightSurfaceHover,
      hoverOpacity: 0.04,
      selected: "rgba(33, 27, 53, 0.12)",
      selectedOpacity: 0.12,
      disabled: locumColors.textDisabled,
      disabledBackground: "rgba(0, 0, 0, 0.12)",
      disabledOpacity: 0.38,
      focus: "rgba(0, 0, 0, 0.06)",
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
    },
  },
  shape: { borderRadius: locumShape.borderRadius },
  shadows,
  typography: {
    fontFamily: locumTypography.fontFamily,
    fontWeightRegular: locumTypography.weightRegular,
    fontWeightMedium: locumTypography.weightMedium,
    fontWeightBold: locumTypography.weightBold,
    h1: { fontWeight: locumTypography.weightBold, fontSize: "clamp(2.4rem, 6vw, 4rem)", lineHeight: 1.1, letterSpacing: "-0.04em" },
    h2: { fontWeight: locumTypography.weightBold, fontSize: "clamp(1.6rem, 3vw, 2.1rem)", lineHeight: 1.2, letterSpacing: "-0.02em" },
    h3: { fontWeight: locumTypography.weightSemiBold, fontSize: "clamp(1.25rem, 2vw, 1.75rem)", lineHeight: 1.25, letterSpacing: "-0.015em" },
    h4: { fontWeight: locumTypography.weightSemiBold, fontSize: "1.25rem", lineHeight: 1.25, letterSpacing: "-0.01em" },
    h5: { fontWeight: locumTypography.weightBold, fontSize: "1.125rem", lineHeight: 1.334 },
    h6: { fontWeight: locumTypography.weightMedium, fontSize: "1rem", lineHeight: 1.6, letterSpacing: "0.01em" },
    subtitle1: { fontWeight: locumTypography.weightMedium, fontSize: "1.125rem", lineHeight: 1.75, letterSpacing: "0.01em" },
    subtitle2: { fontWeight: locumTypography.weightRegular, fontSize: "0.875rem", lineHeight: 1.57, letterSpacing: "0.01em" },
    body1: { fontWeight: locumTypography.weightRegular, fontSize: "1rem", lineHeight: 1.6, letterSpacing: "0.01em" },
    body2: { fontWeight: locumTypography.weightRegular, fontSize: "0.875rem", lineHeight: 1.43, letterSpacing: "0.01em" },
    caption: { fontWeight: locumTypography.weightSemiBold, fontSize: "0.75rem", lineHeight: 1.4, letterSpacing: "0.08em", textTransform: "uppercase" as const },
    overline: { fontWeight: locumTypography.weightSemiBold, fontSize: "0.75rem", lineHeight: 1.4, letterSpacing: "0.08em", textTransform: "uppercase" as const },
    button: { fontWeight: locumTypography.weightBold, fontSize: "0.9375rem", lineHeight: 1, letterSpacing: "0.02em", textTransform: "none" as const },
  },
  components: {
    MuiCssBaseline: { styleOverrides: { html: { scrollBehavior: "smooth" }, body: { color: locumColors.textPrimary, lineHeight: 1.6 } } },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: locumShape.cornerRadius.pill, textTransform: "none" as const, fontWeight: locumTypography.weightBold, fontSize: "0.9375rem", letterSpacing: "0.02em" },
        containedPrimary: { backgroundColor: locumColors.teal, color: locumColors.navy, "&:hover": { backgroundColor: locumColors.tealDark, color: locumColors.white } },
        outlinedPrimary: { borderColor: locumColors.tealBorderSubtle, "&:hover": { backgroundColor: "rgba(34, 213, 213, 0.04)", borderColor: locumColors.tealBorderSubtle } },
        outlinedInherit: { borderColor: locumColors.darkBorderStrong, color: locumColors.textOnDarkMuted, "&:hover": { backgroundColor: locumColors.darkSurfaceHover, borderColor: locumColors.darkBorderStrong } },
        sizeSmall: { fontSize: "0.8125rem", padding: "4px 10px" },
        sizeMedium: { padding: "0.6rem 1.75rem" },
        sizeLarge: { padding: "0.75rem 2rem" },
      },
    },
    MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { rounded: { borderRadius: locumShape.cornerRadius.xl } } },
    MuiCard: { styleOverrides: { root: { borderRadius: locumShape.cornerRadius.xl } } },
    MuiChip: { styleOverrides: { root: { borderRadius: locumShape.borderRadius } } },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: locumShape.borderRadius },
        standardError: { backgroundColor: locumColors.errorLight, color: locumColors.errorDark },
        standardWarning: { backgroundColor: locumColors.warningLight, color: locumColors.warningDark },
        standardInfo: { backgroundColor: locumColors.infoLight, color: locumColors.infoMain },
        standardSuccess: { backgroundColor: locumColors.successLight, color: locumColors.successDark },
      },
    },
    MuiDialog: { defaultProps: { PaperProps: { elevation: 16 } } },
    MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: locumColors.tooltipBg, fontSize: "10px", fontWeight: locumTypography.weightMedium } } },
    MuiLink: { defaultProps: { underline: "none" }, styleOverrides: { root: { textDecoration: "none", "&:hover": { textDecoration: "none" } } } },
    MuiOutlinedInput: { styleOverrides: { root: { borderRadius: locumShape.cornerRadius.pill } } },
    MuiTextField: { defaultProps: { variant: "outlined" } },
    MuiTableHead: { styleOverrides: { root: { "& .MuiTableCell-head": { fontWeight: locumTypography.weightMedium, fontSize: "14px" } } } },
    MuiAppBar: { defaultProps: { elevation: 0, color: "inherit" }, styleOverrides: { root: { backgroundColor: locumColors.white, borderBottom: `1px solid ${locumColors.navBorder}` } } },
  },
});

export const locumDarkTheme = createTheme({
  ...locumTheme,
  palette: {
    ...locumTheme.palette,
    mode: "dark",
    text: { primary: locumColors.textOnDark, secondary: locumColors.textOnDarkSecondary, disabled: locumColors.textOnDarkSubtle },
    background: { default: locumColors.navy, paper: locumColors.navyDeep },
    divider: locumColors.darkBorder,
    action: { ...locumTheme.palette.action, hover: locumColors.darkSurfaceHover, hoverOpacity: 0.08 },
  },
});
